import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { modules, getModule } from '../data/modules';
import { mergeCatalogue, publishedInCourse, moduleNumberIn, courseForModule } from '../data/catalogue';
import { courseHref, moduleHref } from '../data/courses';
import { useConfig } from '../context/ConfigContext';

const phaseColorMap = {
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  rose: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function ModuleLayout({ moduleId, module: provided, children }) {
  const { customModules, allCourses } = useConfig();

  // `provided` is the module ModuleRouter has ALREADY fetched, and accepting
  // it is what makes an AI Studio module render at all.
  //
  // This component used to resolve the module itself with `getModule(moduleId)`
  // — one argument, so only the hardcoded registry in data/modules.js was ever
  // searched. A custom module lives in Firestore and is not in that array, so
  // `mod` came back undefined and the guard below painted "Module not found."
  // over a module that had loaded perfectly well. It is the same shape as the
  // redirect bug in ModuleRouter: a lookup that knows about half the catalogue,
  // deciding on behalf of all of it.
  //
  // The fallback still covers the 25 built-in modules, which pass a numeric
  // `moduleId` and no `module` prop — and it now passes `customModules`, so a
  // built-in module OVERRIDDEN in Studio resolves to the override here too,
  // the same precedence ModuleRouter and the admin pages already apply.
  const mod = provided || getModule(moduleId, customModules);

  const catalogue = mergeCatalogue(modules, customModules || []);

  // WHICH course are we reading this module in? THE URL SAYS.
  //
  // `/courses/<c>/module/<m>` carries the course, so a module in several
  // courses is read in the one you actually came from — the back link and
  // prev/next follow it rather than a guess.
  //
  // `courseForModule` is the fallback for a bare `/module/<id>`: a built-in
  // module component rendering itself, an old bookmark, a cross-course link in
  // module copy. Same guess as before, now in one place shared with the
  // redirect so the two cannot drift apart.
  const { courseId } = useParams();
  const course =
    (allCourses || []).find((c) => String(c.id) === String(courseId)) ||
    courseForModule(moduleId, allCourses, catalogue);

  // Previous/next stay inside that course, and skip anything unpublished —
  // otherwise the last Sheets module links into a half-built Finance one.
  const siblings = course ? publishedInCourse(course, catalogue) : [];
  // Compared as strings on purpose: mergeCatalogue stringifies every id, and a
  // built-in module passes `moduleId` as a number. `===` between the two finds
  // nothing, which would silently drop prev/next from all 25 built-in modules.
  const pos = siblings.findIndex((m) => String(m.id) === String(moduleId));
  const prevModule = pos > 0 ? siblings[pos - 1] : null;
  const nextModule = pos >= 0 && pos < siblings.length - 1 ? siblings[pos + 1] : null;

  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Module not found.</p>
      </div>
    );
  }

  const badgeClass = phaseColorMap[mod.phaseColor] || phaseColorMap.blue;
  // The module's POSITION IN THIS COURSE, so a module shared between two
  // courses is numbered correctly in each. Null when it is in no course at
  // all, in which case the label is omitted rather than reading
  // "Module custom-mtaw6809" — an internal key where a position belongs.
  const number = course && pos >= 0 ? moduleNumberIn(course, pos) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          {/* Back to the course this module belongs to, not the combined list —
              a Finance trainee should never be dropped into the Sheets course. */}
          <Link
            to={courseHref(course?.id || mod.track)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to modules
          </Link>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {number != null && (
                <span className="text-xs font-medium text-gray-400">
                  Module {number}
                </span>
              )}
              <span
                className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${badgeClass}`}
              >
                {mod.phase}
              </span>
            </div>
            <h1 className="text-lg font-bold text-gray-900 truncate">
              {mod.title}
            </h1>
            {mod.subtitle && (
              <p className="text-sm text-gray-500 truncate">{mod.subtitle}</p>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">{children}</main>

      {/* Bottom navigation */}
      <nav className="border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          {prevModule ? (
            <Link
              to={moduleHref(course?.id, prevModule)}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{prevModule.title}</span>
              <span className="sm:hidden">Previous</span>
            </Link>
          ) : (
            <div />
          )}

          {nextModule ? (
            <Link
              to={moduleHref(course?.id, nextModule)}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="hidden sm:inline">{nextModule.title}</span>
              <span className="sm:hidden">Next</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </nav>
    </div>
  );
}
